import { useState, useEffect } from "react";
import { useSetAtom } from "jotai";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { AppleAuthButton } from "@/components/ui/auth/apple-auth-button";
import { GoogleAuthButton } from "@/components/ui/auth/google-auth-button";
import { EmailAuthButton } from "@/components/ui/auth/email-auth-button";
import { LoginForm } from "@/components/forms/LoginForm";
import { LegalLinks } from "@/components/ui/legal-links";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/hooks/useTranslation";
import { PATHS } from "@/config/paths";
import { useRouter } from "@tanstack/react-router";
import { useMobile } from "@/providers/mobileProvider";
import { loginDialog } from "@/state/dialogs";

type LoginDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const LoginDialog = ({ open, onOpenChange }: LoginDialogProps) => {
  const router = useRouter();
  const { isMobile, isAndroid } = useMobile();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const setLoginDialogOpen = useSetAtom(loginDialog);
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) {
      setShowEmailForm(false);
    }
  }, [open]);

  const continueWithEmail = () => {
    if (isMobile) {
      router.navigate({ to: PATHS.LOGIN });
      setLoginDialogOpen(false);
    } else {
      setShowEmailForm(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <AnimatePresence mode="wait">
            {!showEmailForm && (
              <motion.div
                key="login-header"
                initial={{ x: 0, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "-5%", opacity: 0 }}
                transition={{ duration: 0.1, ease: "easeInOut" }}
                className="flex flex-col gap-2"
              >
                <DialogTitle>{t("auth.login.title")}</DialogTitle>
                <DialogDescription>{t("auth.login.description")}</DialogDescription>
              </motion.div>
            )}

            {showEmailForm && (
              <motion.div
                key="email-header"
                initial={{ x: "5%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "5%", opacity: 0 }}
                transition={{ duration: 0.1, ease: "easeInOut" }}
              >
                <DialogTitle className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => setShowEmailForm(false)}
                  >
                    <ArrowLeft />
                  </Button>
                  {t("auth.login.continue_with_email")}
                </DialogTitle>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogHeader>

        <div className="grid gap-6 relative">
          <AnimatePresence mode="wait">
            {!showEmailForm && (
              <motion.div
                key="auth-buttons"
                initial={{ x: 0, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "-5%", opacity: 0 }}
                transition={{ duration: 0.1, ease: "easeInOut" }}
                className="flex flex-col gap-3"
              >
                {!isAndroid && <AppleAuthButton />}
                <GoogleAuthButton />
                <EmailAuthButton onClick={continueWithEmail} />
              </motion.div>
            )}

            {showEmailForm && (
              <motion.div
                key="email-form"
                initial={{ x: "5%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "5%", opacity: 0 }}
                transition={{ duration: 0.1, ease: "easeInOut" }}
              >
                <LoginForm />
              </motion.div>
            )}
          </AnimatePresence>

          <LegalLinks closeDialog={() => onOpenChange(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
